import type { Ctx } from './ctx';
import type { ViewModel } from '@/screens/generated/view-model';
import { api } from '@/client/api';

/** The strategies playbook, server-backed (mandate §5): the screen's two reference cards toggle REAL rows
 *  (both seeds carry 'OB reaction entry' and 'London breakout'), New Strategy creates a real row rendered as
 *  a data-driven card, and every mutation reconciles from a fresh `prepare.strategies.list` read-back.
 *
 *  The two reference cards stay the design's own render (their copy and stats are the package's literals);
 *  their toggles and the created cards are where the server's truth shows. Stats on created cards come from
 *  the server's roll-up of tagged trades — computed on read, which is why "Recalc" is a re-read, honestly. */

/** The two design cards' row names — cards beyond these render data-driven. */
const REFERENCE = ['OB reaction entry', 'London breakout'] as const;

type StratRow = {
  id: string;
  name: string;
  status: 'active' | 'paused' | 'archived';
  rules: string[];
  sessions: string[];
  stats: { trades: number; winRate: number; expectancyR: number };
};

export function buildStrategies(c: Ctx) {
  const S = c.S;
  const ST = S.strat;
  const patch = (part: Partial<NonNullable<typeof ST>>) =>
    c.set((st) => (st.strat ? { strat: { ...st.strat, ...part } } : {}));

  const refetch = async () => {
    const out = await api.prepare.strategies.list();
    patch({
      busy: false,
      items: out.items.map((i) => ({
        id: i.id,
        name: i.name,
        status: i.status,
        rules: [...i.rules],
        sessions: [...i.sessions],
        stats: { ...i.stats },
      })),
    });
  };

  const load = async () => {
    if (S.strat?.loaded || S.strat?.busy) return;
    c.set({ strat: { loaded: false, busy: true, items: [], editor: null } });
    try {
      await refetch();
      patch({ loaded: true });
    } catch {
      patch({ busy: false, loaded: true }); // reference cards still render; writes will surface their errors
    }
  };

  const setStatus = (row: StratRow, label: string) => {
    const to = row.status === 'active' ? 'paused' : 'active';
    void api.prepare.strategies
      .update({ idempotencyKey: `str-${row.id}-${Date.now()}`, id: row.id, status: to })
      .then(async () => {
        await refetch();
        c.flash(`${label} ${to === 'active' ? 'activated' : 'paused'}.`, 'pos');
      })
      .catch((error: unknown) =>
        c.flash(error instanceof Error ? error.message : `${label} could not be changed.`, 'neg'),
      );
  };

  const byName = (name: string) => ST?.items.find((i) => i.name === name && i.status !== 'archived');

  /** The reference cards' toggles: server rows when loaded, the local skeleton before that. */
  const strat = (key: 'ob' | 'ldn') => {
    const name = key === 'ob' ? REFERENCE[0] : REFERENCE[1];
    const row = ST?.loaded ? byName(name) : undefined;
    const on = row ? row.status === 'active' : !!S.stratOn[key];
    const t = c.tog(on);
    return Object.assign({ on }, t, {
      toggle: () => {
        if (!row) {
          c.flash('Strategies are still loading from the server — a moment.', 'mut');
          return;
        }
        setStatus(row, row.name);
      },
    });
  };

  const parseList = (raw: string, max: number) =>
    raw
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(0, max);

  return {
    stratHydrate: () => void load(),
    stratOb: strat('ob'),
    stratLdn: strat('ldn'),
    /** TRADER-CREATED strategies, rendered as data-driven cards. The seeded tagging rows (the names the
     *  dataset's trade-tag vocabulary carries) exist for trade-tagging and are NOT cards — the design's
     *  resting render shows exactly the two reference cards, and visreg holds it to that.
     *
     *  Filtered on `exec.tradeStrategies` ONLY: that vocabulary is carried verbatim on both bindings
     *  (dataset-parity's own note). `ai.playbook` must NOT join the set — the live server DERIVES it from
     *  the active strategies, so a strategy created here would join the playbook on the next dataset fetch
     *  and this filter would swallow its own card (found live). */
    stratCards: (ST?.items ?? [])
      .filter((i) => {
        const seeded = new Set<string>([...c.data.exec.tradeStrategies, ...REFERENCE]);
        return i.status !== 'archived' && !seeded.has(i.name);
      })
      .map((row) => {
        const on = row.status === 'active';
        const t = c.tog(on);
        return {
          id: row.id,
          name: row.name,
          desc: row.rules.join(' · ') || 'No rules declared yet.',
          chips: row.sessions,
          statTrades: String(row.stats.trades),
          statWin: `${row.stats.winRate}%`,
          statExp: `${row.stats.expectancyR >= 0 ? '+' : ''}${row.stats.expectancyR}R`,
          on,
          trackBg: t.trackBg,
          knob: t.knob,
          knobBg: t.knobBg,
          toggle: () => setStatus(row, row.name),
          edit: () =>
            patch({
              editor: {
                id: row.id,
                name: row.name,
                sessions: row.sessions.join(', '),
                rules: row.rules.join(', '),
              },
            }),
          archive: () =>
            void api.prepare.strategies
              .archive({ idempotencyKey: `sta-${row.id}-${Date.now()}`, id: row.id })
              .then(async () => {
                await refetch();
                c.flash(`${row.name} archived.`, 'mut');
              })
              .catch((error: unknown) =>
                c.flash(error instanceof Error ? error.message : `${row.name} could not be archived.`, 'neg'),
              ),
        };
      }),
    stratNew: () => {
      if (!ST?.loaded) {
        c.flash('Strategies are still loading from the server — a moment.', 'mut');
        return;
      }
      patch({ editor: { id: null, name: '', sessions: '', rules: '' } });
    },
    stratRecalc: () => {
      if (!ST?.loaded) {
        c.flash('Strategies are still loading from the server — a moment.', 'mut');
        return;
      }
      patch({ busy: true });
      void refetch()
        .then(() =>
          c.flash(
            'Stats re-read from the ledger — they are computed from your tagged trades on every read.',
            'pos',
          ),
        )
        .catch((error: unknown) => {
          patch({ busy: false });
          c.flash(error instanceof Error ? error.message : 'Stats could not be re-read.', 'neg');
        });
    },
    stratEditorOpen: ST?.editor ?? null,
    stratEditorSet: (field: 'name' | 'sessions' | 'rules', value: string) =>
      c.set((st) =>
        st.strat?.editor ? { strat: { ...st.strat, editor: { ...st.strat.editor, [field]: value } } } : {},
      ),
    stratEditorCancel: () => patch({ editor: null }),
    stratEditorSave: () => {
      const e = ST?.editor;
      if (!e) return;
      if (!e.name.trim()) {
        c.flash('Give the strategy a name.', 'neg');
        return;
      }
      const body = {
        name: e.name.trim(),
        sessions: parseList(e.sessions, 8),
        rules: parseList(e.rules, 20),
      };
      patch({ editor: null, busy: true });
      const done = async (verb: string) => {
        await refetch();
        c.flash(`${body.name} ${verb}.`, 'pos');
      };
      const fail = (error: unknown) => {
        patch({ busy: false });
        c.flash(error instanceof Error ? error.message : 'The strategy could not be saved.', 'neg');
      };
      if (e.id === null)
        void api.prepare.strategies
          .create({ idempotencyKey: `stc-${Date.now()}`, ...body })
          .then(() => done('created'))
          .catch(fail);
      else
        void api.prepare.strategies
          .update({ idempotencyKey: `stu-${e.id}-${Date.now()}`, id: e.id, ...body })
          .then(() => done('updated'))
          .catch(fail);
    },
  } satisfies Partial<ViewModel>;
}
